/**
 * The one definition of "looks like an address we can invite", shared by the
 * Settings invite form (TeamMembersSection) and the onboarding invite step
 * (StepInviteTeam), so the two surfaces cannot disagree about what a valid
 * teammate email is or how they say it is not one.
 *
 * Deliberately loose (a local part, an at sign, a domain with a dot): the
 * server's zod `email()` is
 * the real gate; this only catches the typo before the round trip.
 */
export const INVITE_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const INVALID_INVITE_EMAIL_MESSAGE = 'That doesn’t look like a valid email address.';

/**
 * The invite form's copy, in ONE place. The Account page's form
 * (TeamMembersSection) and the project page's InviteTeamModal render the same
 * action, so they read the same strings and cannot drift.
 */
export const INVITE_FORM_COPY = {
  emailLabel: 'Team member email',
  roleLabel: 'Role',
  emailPlaceholder: 'colleague@example.com',
  submit: 'Send invite',
  submitting: 'Sending...',
  submitDisabledTitle: "Enter a teammate's email address",
  helper: 'We’ll email them a magic link. Invitations expire after 7 days.',
  emptyEmail: 'Enter an email address.',
  sendFailed: 'Failed to send invitation.',
  sent: (email: string): string => `Invitation sent to ${email}.`,
  upgrade: 'Upgrade plan',
} as const;

/**
 * The invite refusals a plan upgrade fixes (routes/teams.ts: the seat cap, and
 * a lapsed trial or subscription). Both forms offer "Upgrade plan" on exactly
 * these, so the owner can act on the sentence that tells them to upgrade.
 */
export function isUpgradeableInviteRefusal(code: string | null | undefined): boolean {
  return code === 'SEAT_LIMIT_REACHED' || code === 'TRIAL_EXPIRED';
}

export const INVITE_ROLE_OPTIONS = [
  { value: 'member', label: 'Member' },
  { value: 'admin', label: 'Admin' },
];

/**
 * Why this address cannot be invited, in the form's own sentence, or null when
 * it can. One judge for both surfaces (the Account page form and the project
 * page's InviteTeamModal), so they refuse the same input in the same words.
 */
export function inviteEmailError(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return INVITE_FORM_COPY.emptyEmail;
  if (!INVITE_EMAIL_RE.test(trimmed)) return INVALID_INVITE_EMAIL_MESSAGE;
  return null;
}

export function isValidInviteEmail(value: string): boolean {
  return INVITE_EMAIL_RE.test(value.trim());
}

/**
 * What Finish actually sends: trimmed, lowercased, blanks dropped, duplicates
 * collapsed (the server lowercases too, so two spellings of one address would
 * otherwise cost two seats in the pending list).
 */
export function normalizeInviteEmails(emails: readonly string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of emails) {
    const email = raw.trim().toLowerCase();
    if (!email || seen.has(email)) continue;
    seen.add(email);
    out.push(email);
  }
  return out;
}
